import {RegisterOptions} from "react-hook-form";
import {CmsCta} from "@lib/Cms/CmsModels";

export enum FORM_TYPE {
    REGISTRATION = "WEB-INS",
    LOGIN = "WEB-LGN",
    RECOVER_PASSWORD = "WEB-RSU",
    RESET_PASSWORD = "WEB-RSP",
    SSO_FINALIZATION = "SSO-FIN",
    WEB_FINALIZATION = "WEB-FIN",
    FINALIZATION = "FIN",
    PROFILE = "WEB-UPD",
    ACTIVATE_ACCOUNT = "ACTIVATE",

    EVENT = "EVENT",
    CONTEST_EVENT = "CONTEST_EVENT",
    REDEMPTION = "REDEMPTION",
}

export enum FORM_FIELD_TYPE {
    OBJECT = "JOBJECT",
    JINVITATION = "JINVITATION",
    TEXT = "JTEXT",
    NUMBER = "JNUMBER",
    EMAIL = "JEMAIL",
    PASSWORD = "JPASSWORD",
    PHONE = "JPHONE",
    ADDRESS = "JADDRESS",
    COUNTRY = "JCOUNTRY",
    TEXTAREA = "JTEXTAREA",
    SELECT_SINGLE = "JSELECT",
    SELECT_MULTIPLE = "JSELECTMULTIPLE",
    RADIO = "JRADIO",
    PRIVACY = "JPRIVACY",
    CHECKBOX = "JCHECKBOX",
    DATEPICKER_SINGLE = "JDATE",
    DATEPICKER_RANGE = "JDATERANGE",
    DATETIMEPICKER_SINGLE = "JDATETIME",
    DATETIMEPICKER_RANGE = "JDATETIMERANGE",
    FILE = "JFILE",
    AVATAR = "JAVATAR",
    HIDDEN = "JHIDDEN",
}

export interface FormFieldConfigurationOptionLabel {
    logicId?: string;
    title?: string;
    text?: string;
    imageUrl?: string;
}

export interface FormFieldConfigurationOption {
    value: string;
    label?: FormFieldConfigurationOptionLabel;
}

export interface FormFieldConfigurationFileOptions {
    multiple?: boolean;
    enableCrop?: boolean;
    minimal?: boolean;
    accept?: string;
    maxCount?: number;
    maxSize?: number;
}

export interface FormFieldConfigurationDatePickerOptions {
    minDate?: "TODAY" | string;
    maxDate?: "TODAY" | string;
}

// @TODO: missing on API
export interface FormFieldConfigurationPhoneOptions {
    phoneWithDialCode?: boolean;
    phoneShowDialCode?: boolean;
    phoneShowCountry?: boolean;
    phoneAllowCountrySelection?: boolean;
    phoneDefaultCountry?: any;
}

// @TODO: missing on API
export interface FormFieldConfigurationSelectOptions {
    countryShowFlags?: boolean;
    searchable?: boolean;
    closeOnSelection?: boolean;
}

// @TODO: missing on API
export interface FormFieldConfigurationOptionsFieldOptions {
    displayMode?: "default" | "alternative";
}

export interface FormFieldConfigurationCmsConfig extends FormFieldConfigurationFileOptions, FormFieldConfigurationDatePickerOptions, FormFieldConfigurationPhoneOptions, FormFieldConfigurationSelectOptions, FormFieldConfigurationOptionsFieldOptions {
    options: FormFieldConfigurationOption[];
}

export interface FormFieldConfigurationCmsTooltip {
    title?: string;
}

export interface FormFieldConfigurationCms {
    tenant?: string;
    project?: string;
    flowHurlId?: string;
    language: string;
    logicId: string;
    hurlId: string;
    hidden: boolean;
    order?: number;
    title?: string;
    text?: string;
    additionalText?: string;
    tooltip?: FormFieldConfigurationCmsTooltip;
    imageUrl?: string;
    validationMessage?: string;

    // @TODO: missing on API
    componentConfig?: FormFieldConfigurationCmsConfig;
    labelTooltip?: FormFieldConfigurationCmsTooltip;
    requiredMessage?: string;
    attachments?: CmsCta[];
}

export interface FormFieldConfiguration {
    logicId?: string;
    hurlId: string;
    type: FORM_FIELD_TYPE;
    mandatory: boolean;
    username: boolean;
    confirmed: boolean;
    readOnly: boolean;
    isGdpr: boolean;
    children?: FormFieldConfiguration[];
    validationFunction?: string;
    cmsConfig?: FormFieldConfigurationCms;
    values?: string[];
    defaultValues?: string[];
}

export interface FormFieldsConfiguration {
    fields: FormFieldConfiguration[]
}

/**
 * Form layout configuration
 * -- Step
 * ---- Group
 * -------- type
 * -------- order
 * -------- rows
 * -------- ....
 * ---- Options
 */

// @TODO: missing on API
export interface FormStepOptions {
    showRequiredFieldsDisclaimer?: boolean;
    requiredFieldsDisclaimer?: string;
    actionsAlignment?: "left" | "center" | "right";
    secondaryActionsAlignment?: "left" | "center" | "right";
    disclaimerAlignment?: "left" | "center" | "right";
    contentAlignment?: "left" | "center" | "right";
    submitCtaBeforeContent?: string;
    submitCtaAfterContent?: string;
}

// @TODO: missing on API
export enum FORM_GROUP_TYPE {
    CONFIGURED = "CONFIGURED",
    DEFAULT = "DEFAULT",
    HIDDEN = "HIDDEN",
    UNMAPPED = "UNMAPPED",
    INVITATION = "INVITATION",
}

export const FORM_STEP_GROUPS = [
    FORM_GROUP_TYPE.INVITATION,
]

export interface FormGroupConfiguration {
    order?: number;
    type?: FORM_GROUP_TYPE;
    title?: string;
    subTitle?: string;
    description?: string;
    textAlignment?: "left" | "center" | "right";
    tooltip?: FormFieldConfigurationCmsTooltip;
    rows: string[][];
}

export interface FormGroup extends Omit<FormGroupConfiguration, "rows"> {
    rows: FormField[][];
}

export enum FORM_STEP_TYPE {
    REG_INVITATION = "INVITATION_CODE",
    MAIN = "MAIN",
}

export interface FormStepConfiguration {
    order: number;
    type: FORM_STEP_TYPE;
    fieldNames?: string[] | null;
    groups: FormGroupConfiguration[];
    options: FormStepOptions;
    submitCta: CmsCta;
    additionalCtas?: CmsCta[];
}

export interface FormStep extends Omit<FormStepConfiguration, "groups" | "options"> {
    layout: FormLayout;
}

export interface FormLayout {
    fields: FormField[];
    groups: FormGroup[];
    options?: FormStepOptions;
    initialState: FormFieldsState;
    canBeRendered: boolean;
    initialized: boolean;
}

/**
 * Normalized models
 */
export interface FormFieldOptionValue extends Record<string, any> {
    label: string;
    value: string;
}

export type FormFieldValue =
    string
    | string[]

export type FormFieldsState = Record<string, FormFieldValue>

export interface FormFieldPayload {
    hurlId: string;
    type: FORM_FIELD_TYPE;
    values: string[] | null;
    children?: FormFieldPayload[];
}

export interface FormPayload {
    fields: FormFieldPayload[];
    clientId?: string;
    hash?: string;
    [key: string]: any;
}

export type FormFieldRules = Omit<RegisterOptions<FormFieldsState, string>, "disabled" | "valueAsNumber" | "valueAsDate" | "setValueAs"> | undefined;

export interface FormFieldElements {
    label: boolean;
    labelTooltip: boolean;
    inputTooltip: boolean;
    description: boolean;
    error: boolean;
}

export interface FormField {
    order: number;
    name: string;
    id: string;
    type: FORM_FIELD_TYPE;
    value?: FormFieldValue;
    options?: FormFieldOptionValue[];
    children: FormField[];
    parent?: Omit<FormField, "children">;
    duplicateParent?: Omit<FormField, "children">;

    required: boolean;
    confirm: boolean;
    hidden: boolean;
    readonly: boolean;

    //rules: FormFieldRules;
    validationMessage?: string;
    requiredMessage?: string;

    label: string;
    labelTooltip?: string;
    placeholder?: string;
    description?: string;
    inputTooltip?: string;
    attachments: CmsCta[];

    canBeRendered: boolean;
    transformValue: (formValue?: FormFieldValue) => string[] | null;
    elementsVisibility: FormFieldElements;
    raw: FormFieldConfiguration;
}

export interface NormalizedField {
    hurlId: string;
    type: FORM_FIELD_TYPE;
    required: boolean;
    hidden: boolean;
    readOnly: boolean;
    label: string;
    description?: string;
    options?: { value: string; label: string }[];
}

/**
 * Fields that have options configured, so we need to normalize the options to FormFieldOptionValue[]
 */
export const FORM_FIELDS_WITH_OPTIONS = [
    FORM_FIELD_TYPE.SELECT_SINGLE,
    FORM_FIELD_TYPE.SELECT_MULTIPLE,
    FORM_FIELD_TYPE.RADIO,
    FORM_FIELD_TYPE.PRIVACY,
    FORM_FIELD_TYPE.CHECKBOX
];

/**
 * Fields that have dates configured
 */
export const FORM_FIELDS_WITH_DATES = [
    FORM_FIELD_TYPE.DATEPICKER_SINGLE,
    FORM_FIELD_TYPE.DATETIMEPICKER_SINGLE
];

/**
 * Fields that have a string form value (the phone is handled separately)
 * Needed to create default required validation
 */
export const FORM_FIELDS_WITH_STRING_VALUES = [
    FORM_FIELD_TYPE.HIDDEN,
    FORM_FIELD_TYPE.JINVITATION,
    FORM_FIELD_TYPE.TEXT,
    FORM_FIELD_TYPE.TEXTAREA,
    FORM_FIELD_TYPE.NUMBER,
    FORM_FIELD_TYPE.PASSWORD,
    FORM_FIELD_TYPE.EMAIL,
    FORM_FIELD_TYPE.RADIO,
    FORM_FIELD_TYPE.PRIVACY,
    FORM_FIELD_TYPE.DATEPICKER_SINGLE,
    FORM_FIELD_TYPE.DATETIMEPICKER_SINGLE,
    FORM_FIELD_TYPE.COUNTRY,
    FORM_FIELD_TYPE.SELECT_SINGLE,
];

/**
 * Fields that have string[] form value
 * Needed to create custom required validation
 */
export const FORM_FIELDS_WITH_STRING_ARRAY_VALUES = [
    FORM_FIELD_TYPE.DATEPICKER_RANGE,
    FORM_FIELD_TYPE.DATETIMEPICKER_RANGE,
    FORM_FIELD_TYPE.CHECKBOX,
    FORM_FIELD_TYPE.SELECT_MULTIPLE,
    FORM_FIELD_TYPE.FILE,
    FORM_FIELD_TYPE.AVATAR
];

export const FORM_FIELDS_WITHOUT_DEFAULT_LABEL_ELEMENT = [
    FORM_FIELD_TYPE.HIDDEN,
];

export const FORM_FIELDS_WITHOUT_DEFAULT_LABEL_TOOLTIP_ELEMENT = [
    FORM_FIELD_TYPE.HIDDEN,
];

export const FORM_FIELDS_WITHOUT_DEFAULT_INPUT_TOOLTIP_ELEMENT = [
    FORM_FIELD_TYPE.CHECKBOX,
    FORM_FIELD_TYPE.RADIO,
    FORM_FIELD_TYPE.PRIVACY,
    FORM_FIELD_TYPE.HIDDEN,
    FORM_FIELD_TYPE.FILE,
    FORM_FIELD_TYPE.AVATAR
];

export const FORM_FIELDS_WITHOUT_DEFAULT_DESCRIPTION_ELEMENT = [
    FORM_FIELD_TYPE.HIDDEN,
];

export const FORM_FIELDS_WITHOUT_DEFAULT_ERROR_ELEMENT = [
    FORM_FIELD_TYPE.HIDDEN,
];